import * as THREE from "three";
import { getIntersects } from "./intersection.js";
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer.js";
import {
    KEYPOINT_LABEL_X_DISPLACEMENT,
    CONNECTED_POINT_PAIRS
} from "../config.js";

function labelNameToPointName(name) {
    return name.replace("keypoint-label-", "keypoint-point-");
}

function pointNameToLabelName(name) {
    return name.replace("keypoint-point-", "keypoint-label-");
}

function createLabel(state, position, className, toothId, labelName) {
    const labelDiv = document.createElement("div");
    labelDiv.className = "keypoint-label";
    labelDiv.textContent =
        className +
        (toothId != null && state?.showToothIDForKeypoints
            ? ` (${toothId})`
            : "");
    labelDiv.style.backgroundColor = "rgba(0,0,0,0.7)";
    labelDiv.style.color = "white";
    labelDiv.style.padding = "5px";
    labelDiv.style.borderRadius = "3px";
    labelDiv.style.pointerEvents = "none";

    const label = new CSS2DObject(labelDiv);
    // Shift label position slightly up and to the right of keypoint
    const labelPosition = position.clone().add(new THREE.Vector3(0.3, 0.3, 0));
    label.position.copy(labelPosition);
    label.position.x += KEYPOINT_LABEL_X_DISPLACEMENT;
    label.name = labelName;
    label.visible = state.showKeypointLabels;
    return label;
}

async function addKeypoint(state, event) {
    if (!state.mesh) return;

    const intersects = getIntersects(state, event);
    if (intersects.length > 0) {
        const point = intersects[0].point;
        const vertexId = intersects[0].face.a; // Get vertex ID from intersection
        const toothId = state.vertexToToothID
            ? state?.vertexToToothID?.get(vertexId)
            : null;
        const fileName = state.objFiles[state.currentFileIndex].name;
        if (!state.keypoints[fileName]) {
            state.keypoints[fileName] = [];
        }

        const currentClass = state.annotationClasses.find(
            (c) => c.id === state.currentClassId
        );

        // Create unique label name using uuid
        const uuid = crypto.randomUUID();
        const pointName = `keypoint-point-${uuid}`;
        const labelName = `keypoint-label-${uuid}`;

        // Store keypoint data including x,y,z coordinates, class name, point name, and tooth ID
        const keypointData = {
            x: point.x,
            y: point.y,
            z: point.z,
            className: currentClass.name,
            pointName: pointName,
            toothId: toothId,
            color: currentClass.color
        };
        state.keypoints[fileName].push(keypointData);

        // Create and add sphere to visualize keypoint
        const sphereGeometry = new THREE.SphereGeometry(0.25, 16, 16);
        const sphereMaterial = new THREE.MeshBasicMaterial({
            color: currentClass.color
        });
        const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
        sphere.position.copy(point);
        sphere.name = pointName;
        sphere.userData.className = currentClass.name;
        sphere.userData.pointName = pointName;
        state.scene.add(sphere);

        // Create and add label
        const label = createLabel(
            state,
            point,
            currentClass.name,
            toothId,
            labelName
        );
        state.scene.add(label);

        // Update keypoint list UI
        renderKeypointList(state, fileName);
    }

    drawMarginalRidgeLines(state);
}

function removeKeypoint(state, point) {
    // Remove label from scene
    const labelToRemove = state.scene.children.find(
        (child) => child.name === pointNameToLabelName(point.name)
    );
    if (labelToRemove) {
        state.scene.remove(labelToRemove);
    }

    // Remove keypoint from keypoints array
    const fileName = state.objFiles[state.currentFileIndex].name;
    state.keypoints[fileName] = state.keypoints[fileName].filter(
        (keypoint) => keypoint.pointName !== point.name
    );

    // Remove keypoint from scene
    state.scene.remove(point);
}

function drawMarginalRidgeLines(state) {
    // Remove existing lines
    state.scene.children = state.scene.children.filter(
        (child) => !child.name?.startsWith("keypoint-line-")
    );
    if (!state.showMarginalRidgeLines || state.labelingMode != "keypoint") {
        return;
    }

    const fileName = state.objFiles[state.currentFileIndex]?.name;
    if (!fileName) return;
    const fileKeypoints = state.keypoints[fileName];
    if (!fileKeypoints) return;
    const lines = [];

    // Draw lines connecting point pairs with same tooth ID
    for (let i = 0; i < fileKeypoints.length; i++) {
        for (let j = i + 1; j < fileKeypoints.length; j++) {
            const keypoint1 = fileKeypoints[i];
            const keypoint2 = fileKeypoints[j];
            if (keypoint1.toothId != keypoint2.toothId) continue;

            // Check if points are a valid pair to connect
            const isPair = CONNECTED_POINT_PAIRS.some(
                ([p1, p2]) =>
                    (keypoint1.className === p1 &&
                        keypoint2.className === p2) ||
                    (keypoint1.className === p2 && keypoint2.className === p1)
            );

            if (isPair) {
                const start = new THREE.Vector3(
                    keypoint1.x,
                    keypoint1.y,
                    keypoint1.z
                );
                const end = new THREE.Vector3(
                    keypoint2.x,
                    keypoint2.y,
                    keypoint2.z
                );

                // Project line endpoints to screen space
                const startScreen = start.clone().project(state.camera);
                const endScreen = end.clone().project(state.camera);

                // Get distance to camera for depth sorting
                const midpoint = new THREE.Vector3()
                    .addVectors(start, end)
                    .multiplyScalar(0.5);
                const distanceToCamera = midpoint.distanceTo(
                    state.camera.position
                );

                const lineGeometry = new THREE.BufferGeometry().setFromPoints([
                    start,
                    end
                ]);
                const lineMaterial = new THREE.LineBasicMaterial({
                    color: 0xffff00,
                    linewidth: 2,
                    transparent: false,
                    depthTest: false
                });
                const line = new THREE.Line(lineGeometry, lineMaterial);
                line.name = `keypoint-line-${keypoint1.pointName}-${keypoint2.pointName}`;
                line.renderOrder = 999;
                lines.push({
                    line,
                    distanceToCamera,
                    startScreen,
                    endScreen,
                    keypoint1,
                    keypoint2
                });
            }
        }
    }

    // Sort lines by distance to camera (furthest first)
    lines.sort((a, b) => b.distanceToCamera - a.distanceToCamera);

    // Filter out overlapping lines, keeping near segments
    const visibleLines = [];
    lines.forEach((lineInfo) => {
        // Remove any existing overlapping lines that are further away
        for (let i = visibleLines.length - 1; i >= 0; i--) {
            const existingLine = visibleLines[i];

            // Get line segments in screen space
            const line1Start = lineInfo.startScreen;
            const line1End = lineInfo.endScreen;
            const line2Start = existingLine.startScreen;
            const line2End = existingLine.endScreen;

            // Skip if keypoints are in the same jaw side
            const toothId1 = lineInfo.keypoint1.toothId;
            const toothId2 = existingLine.keypoint1.toothId;
            if (
                toothId1 &&
                toothId2 &&
                toothId1?.toString()?.length > 1 &&
                toothId2?.toString()?.length > 1 &&
                toothId1.toString().slice(0, 1) ==
                    toothId2.toString().slice(0, 1)
            ) {
                continue;
            }

            // Check if bounding boxes overlap
            const x1 = line1Start.x;
            const y1 = line1Start.y;
            const x2 = line1End.x;
            const y2 = line1End.y;
            const x3 = line2Start.x;
            const y3 = line2Start.y;
            const x4 = line2End.x;
            const y4 = line2End.y;

            const minX1 = Math.min(x1, x2);
            const maxX1 = Math.max(x1, x2);
            const minY1 = Math.min(y1, y2);
            const maxY1 = Math.max(y1, y2);
            const minX2 = Math.min(x3, x4);
            const maxX2 = Math.max(x3, x4);
            const minY2 = Math.min(y3, y4);
            const maxY2 = Math.max(y3, y4);

            const overlaps = !(
                maxX1 < minX2 ||
                maxX2 < minX1 ||
                maxY1 < minY2 ||
                maxY2 < minY1
            );

            if (overlaps) {
                // If current line is closer, remove the existing line
                if (lineInfo.distanceToCamera < existingLine.distanceToCamera) {
                    visibleLines.splice(i, 1);
                } else {
                    // Current line is further, skip adding it
                    continue;
                }
            }
        }

        // Add the current line if it wasn't blocked by a closer line
        visibleLines.push(lineInfo);
    });

    // Add visible lines to scene
    visibleLines.forEach(({ line }) => {
        state.scene.add(line);
    });
}

function drawKeypoints(state, fileName) {
    if (!state || !fileName) return;
    // Remove all keypoint-related objects from the scene
    for (let i = state.scene.children.length - 1; i >= 0; i--) {
        const child = state.scene.children[i];
        if (child.name?.startsWith("keypoint-")) {
            state.scene.remove(child);
        }
    }

    // Draw new keypoint spheres with attached labels
    const fileKeypoints = state.keypoints[fileName];
    if (fileKeypoints && Array.isArray(fileKeypoints)) {
        fileKeypoints.forEach((keypoint) => {
            const keypointClass = state.annotationClasses.find(
                (c) => c.name === keypoint.className
            );

            if (!keypointClass) return;

            const uuid = crypto.randomUUID();
            const pointName = keypoint.pointName || `keypoint-point-${uuid}`;
            const labelName = pointNameToLabelName(pointName);

            // Create sphere for keypoint
            const sphereGeometry = new THREE.SphereGeometry(0.25, 16, 16);
            const sphereMaterial = new THREE.MeshBasicMaterial({
                color: keypointClass.color
            });
            const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
            const position = new THREE.Vector3(
                keypoint.x,
                keypoint.y,
                keypoint.z
            );
            sphere.position.copy(position);
            sphere.name = pointName;
            sphere.userData.className = keypoint.className;
            sphere.userData.keypointData = keypoint;
            sphere.userData.pointName = pointName;
            state.scene.add(sphere);

            // Create and add label
            const label = createLabel(
                state,
                position,
                keypointClass.name,
                keypoint.toothId,
                labelName
            );
            state.scene.add(label);
        });

        drawMarginalRidgeLines(state, fileKeypoints);
    }
}

function drawSelectionBox(state, keypoint) {
    // Remove any existing selection box
    state.scene.children = state.scene.children.filter(
        (child) => child.name !== "keypoint-selection-box"
    );

    // Create box geometry slightly larger than the keypoint sphere
    const boxGeometry = new THREE.BoxGeometry(0.5, 0.5, 0.5);
    const boxMaterial = new THREE.LineBasicMaterial({
        color: 0xffff00,
        depthTest: false // Disable depth testing so selection box is always visible
    });
    const boxWireframe = new THREE.LineSegments(
        new THREE.EdgesGeometry(boxGeometry),
        boxMaterial
    );
    boxWireframe.position.copy(keypoint.position);
    boxWireframe.name = "keypoint-selection-box";
    boxWireframe.renderOrder = 1000; // Ensure selection box renders on top of everything
    state.scene.add(boxWireframe);
}

function deselectKeypoint(state) {
    // Remove keypoint selection
    state.scene.children = state.scene.children.filter(
        (child) => child.name !== "keypoint-selection-box"
    );
    state.selectionBox = null;
    state.selectedKeypoint = null;

    // Remove highlight from all keypoint items
    document.querySelectorAll(".keypoint-item").forEach((item) => {
        item.style.backgroundColor = "transparent";
    });
}

function selectKeypoint(state, keypoint) {
    // If clicking the same keypoint that's already selected, deselect it
    if (state.selectedKeypoint === keypoint) {
        deselectKeypoint(state);
        return;
    }

    // Remove existing selection box if any
    deselectKeypoint(state);
    state.selectedKeypoint = keypoint;

    // Create new selection box after setting selected keypoint
    drawSelectionBox(state, keypoint);

    // Find and scroll to the corresponding keypoint item in the list
    const keypointItems = document.querySelectorAll(".keypoint-item");
    const fileName = state.objFiles[state.currentFileIndex].name;
    const keypointIndex = state.keypoints[fileName].findIndex(
        (kp) => kp.pointName === keypoint.userData.pointName
    );
    if (keypointIndex >= 0) {
        // Highlight the selected keypoint in the list
        keypointItems.forEach((item) => {
            item.style.backgroundColor = "transparent";
        });
        keypointItems[keypointIndex].style.backgroundColor =
            "rgba(255, 255, 255, 0.2)";
        keypointItems[keypointIndex].scrollIntoView({
            behavior: "smooth",
            block: "nearest"
        });
    }
}

function renderKeypointList(state, fileName) {
    const keypointContainer = document.getElementById(
        "inner-keypoints-container"
    );
    keypointContainer.innerHTML = "";

    if (state.keypoints[fileName]) {
        state.keypoints[fileName].forEach((keypoint, index) => {
            const keypointDiv = document.createElement("div");
            keypointDiv.className = "keypoint-item";

            // Get color from annotation class if not stored in keypoint
            const keypointClass = state.annotationClasses.find(
                (c) => c.name === keypoint.className
            );
            const color =
                keypoint.color ||
                (keypointClass ? keypointClass.color : "#ccc");
            keypoint.color = color; // Store color in keypoint for future use

            keypointDiv.style.borderLeft = `4px solid ${color}`;

            // Highlight if this keypoint is selected
            if (
                state.selectedKeypoint &&
                state.selectedKeypoint.userData.pointName === keypoint.pointName
            ) {
                keypointDiv.style.backgroundColor = "rgba(255, 255, 255, 0.2)";
            }

            // Make the div clickable to select keypoint
            keypointDiv.style.cursor = "pointer";
            keypointDiv.addEventListener("click", () => {
                const keypointObj = state.scene.getObjectByName(
                    keypoint.pointName
                );
                if (keypointObj) {
                    selectKeypoint(state, keypointObj);
                }
            });

            // Add ID display
            const idSpan = document.createElement("span");
            idSpan.textContent = `#${index + 1}`;
            idSpan.style.marginRight = "4px";
            idSpan.style.color = "#999";

            // Add class select
            const classSelect = document.createElement("select");
            classSelect.style.width = "120px";
            classSelect.style.marginRight = "4px";

            // Add class options
            state.annotationClasses.forEach((annotationClass) => {
                const option = document.createElement("option");
                option.value = annotationClass.name;
                option.textContent = annotationClass.name;
                if (keypoint.className === annotationClass.name) {
                    option.selected = true;
                }
                classSelect.appendChild(option);
            });

            classSelect.addEventListener("change", (e) => {
                keypoint.className = e.target.value;
                const selectedClass = state.annotationClasses.find(
                    (c) => c.name === e.target.value
                );
                if (selectedClass) {
                    keypoint.color = selectedClass.color;
                    keypointDiv.style.borderLeft = `4px solid ${selectedClass.color}`;

                    // Update keypoint visualization
                    const keypointObj = state.scene.getObjectByName(
                        keypoint.pointName
                    );
                    if (keypointObj) {
                        keypointObj.material.color = new THREE.Color(
                            selectedClass.color
                        );
                    }
                }
                if (state.autoSave) {
                    saveLabels(state);
                }
                drawKeypoints(state, fileName);
            });

            // Add tooth ID select
            const toothSelect = document.createElement("select");
            toothSelect.style.width = "80px";
            toothSelect.style.marginLeft = "4px";

            // Add options
            const options = [
                { value: "0", text: "Unknown" },
                ...Array.from({ length: 8 }, (_, i) => ({
                    value: `1${i + 1}`,
                    text: `Tooth 1${i + 1}`
                })),
                ...Array.from({ length: 8 }, (_, i) => ({
                    value: `2${i + 1}`,
                    text: `Tooth 2${i + 1}`
                })),
                ...Array.from({ length: 8 }, (_, i) => ({
                    value: `3${i + 1}`,
                    text: `Tooth 3${i + 1}`
                })),
                ...Array.from({ length: 8 }, (_, i) => ({
                    value: `4${i + 1}`,
                    text: `Tooth 4${i + 1}`
                }))
            ];

            options.forEach((opt) => {
                const option = document.createElement("option");
                option.value = opt.value;
                option.textContent = opt.text;
                toothSelect.appendChild(option);
            });

            toothSelect.value = keypoint.toothId || "0";
            toothSelect.addEventListener("change", (e) => {
                keypoint.toothId = parseInt(e.target.value);
                if (state.autoSave) {
                    saveLabels(state);
                }
                drawKeypoints(state, fileName);
            });

            keypointDiv.appendChild(idSpan);
            keypointDiv.appendChild(classSelect);
            keypointDiv.appendChild(toothSelect);
            keypointContainer.appendChild(keypointDiv);
        });
    }
}

function updateKeypointLabelsVisibility(state, isVisible) {
    // Find all keypoint label objects in the scene
    const keypointLabels = state.scene.children.filter((child) =>
        child.name?.startsWith("keypoint-label-")
    );

    // Update their visibility based on showKeypointLabels state
    keypointLabels.forEach((label) => {
        label.visible = isVisible;
    });
}

export {
    addKeypoint,
    removeKeypoint,
    drawKeypoints,
    drawSelectionBox,
    labelNameToPointName,
    pointNameToLabelName,
    deselectKeypoint,
    selectKeypoint,
    renderKeypointList,
    updateKeypointLabelsVisibility,
    drawMarginalRidgeLines
};
